/**
 * HexCell - 単一ヘックスの表示コンポーネント
 */

import React from 'react';
import type { Hex, GoodsColor, TileType } from '../../types';
import { getHexPoints, getTerrainFill, getGoodsColorFill, getCityColorFill, getTrackColorsByOwners } from './utils';
import { renderTileTracks } from './trackRenderer';

/**
 * 単一ヘックスの表示Props
 */
export interface HexCellProps {
  hex: Hex;
  centerX: number;
  centerY: number;
  hexSize: number;
  rotationAngle: number;
  onClick?: () => void;
  /** 右クリックハンドラ（反時計回り回転用） */
  onRightClick?: () => void;
  isTemporary?: boolean;
  temporaryTileType?: TileType;
  temporaryRotation?: number;
  isClickable?: boolean;
  hasValidationError?: boolean;
  validationErrorMessage?: string | null;
  /** 都市/新都市がハイライト対象かどうか */
  isHighlightedCity?: boolean;
  /** レンダリング時に商品を描画しない（別レイヤーで描画するため） */
  skipGoodsRendering?: boolean;
  /** このヘクスの線路がハイライト対象かどうか */
  isHighlightedTrack?: boolean;
  /** 強調するEdgeペアのリスト（クロス線路で特定ペアのみ強調する場合）、segmentIndex付き */
  highlightedEdgePairs?: Array<{ pair: [number, number]; segmentIndex: number }>;
  /** 経路の総セグメント数（ウェーブエフェクト用） */
  totalHighlightCount?: number;
  /** 都市名表示をスキップ（別レイヤーで描画するため） */
  skipCityNameRendering?: boolean;
}

/**
 * 新都市タイルタイプから情報を抽出
 */
function getNewCityInfo(tileType: TileType | string | undefined): { isNewCity: boolean; name: string | null; color: string | null } {
  if (!tileType) return { isNewCity: false, name: null, color: null };

  // NEW_CITY_A, NEW_CITY_B, ... NEW_CITY_H にマッチ
  if (!tileType.startsWith('NEW_CITY_')) return { isNewCity: false, name: null, color: null };

  const name = tileType.charAt(9); // "NEW_CITY_X" の X を取得
  if (!/^[A-H]$/.test(name)) return { isNewCity: false, name: null, color: null };

  const colorMap: Record<string, string> = {
    'A': 'red',
    'B': 'blue',
    'C': 'black',
    'D': 'black',
    'E': 'yellow',
    'F': 'purple',
    'G': 'black',
    'H': 'black',
  };

  return { isNewCity: true, name, color: colorMap[name] || null };
}

export function HexCell({
  hex,
  centerX,
  centerY,
  hexSize,
  rotationAngle,
  onClick,
  onRightClick,
  isTemporary,
  temporaryTileType,
  temporaryRotation,
  isClickable,
  hasValidationError,
  validationErrorMessage: _validationErrorMessage,
  isHighlightedCity,
  skipGoodsRendering,
  isHighlightedTrack,
  highlightedEdgePairs,
  totalHighlightCount,
  skipCityNameRendering: _skipCityNameRendering,
}: HexCellProps) {
  const points = getHexPoints(centerX, centerY, hexSize);

  // 新都市タイル判定（TILE_DEFINITIONS を使わず type 名から判定）
  const placedNewCityInfo = getNewCityInfo(hex.tile?.type);
  const isNewCity = placedNewCityInfo.isNewCity;
  const newCityName = placedNewCityInfo.name;

  // 新都市の色を取得（getNewCityInfo を再利用）
  const newCityColor = placedNewCityInfo.color;

  // 仮配置タイルの新都市判定
  const tempNewCityInfo = getNewCityInfo(temporaryTileType);
  const isTempNewCity = isTemporary && tempNewCityInfo.isNewCity;

  // 商品キューブのサイズ（2倍に拡大: 6→12）
  const cubeSize = 12;
  const cubeSpacing = cubeSize + 2;

  // 右クリックハンドラ（メニュー無効化 + 反時計回り回転）
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onRightClick) {
      onRightClick();
    }
  };

  // 都市が複数色かどうかを判定
  const cityColors = hex.city && Array.isArray(hex.city.color) ? hex.city.color : null;
  const isMultiColorCity = cityColors && cityColors.length > 1;
  const hexId = `hex-${hex.coordinate.q}-${hex.coordinate.r}`;

  // 扇形のclipPath用パスを生成（北=上から時計回り）
  const createPieSlicePath = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number): string => {
    // 角度をラジアンに変換（北=上が0°、時計回りが正）
    // SVG座標系: x右が正、y下が正
    // コンパス角θ → SVG座標: x = sin(θ), y = -cos(θ)
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + radius * Math.sin(startRad);
    const y1 = cy - radius * Math.cos(startRad);
    const x2 = cx + radius * Math.sin(endRad);
    const y2 = cy - radius * Math.cos(endRad);

    // largeArcFlag: 180度より大きい場合は1
    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;

    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  return (
    <g
      className={`hex-cell ${isClickable ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      onContextMenu={handleContextMenu}
    >
      {/* 複数色都市用のclipPath定義（扇形で均等分割） */}
      {isMultiColorCity && (
        <defs>
          {cityColors.map((_, idx) => {
            const totalColors = cityColors.length;
            const anglePerSlice = 360 / totalColors;
            const startAngle = idx * anglePerSlice;
            const endAngle = (idx + 1) * anglePerSlice;
            const radius = hexSize * 2; // ヘクスを十分覆う半径
            return (
              <clipPath key={`clip-${idx}`} id={`${hexId}-slice-${idx}`}>
                <path d={createPieSlicePath(centerX, centerY, radius, startAngle, endAngle)} />
              </clipPath>
            );
          })}
        </defs>
      )}

      {/* Hex本体 - 複数色都市の場合は扇形で描画 */}
      {isMultiColorCity ? (
        <>
          {cityColors.map((color, idx) => (
            <polygon
              key={`color-${idx}`}
              className="transition-all duration-150"
              style={isHighlightedCity ? { animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
              points={points}
              fill={getCityColorFill(color)}
              clipPath={`url(#${hexId}-slice-${idx})`}
            />
          ))}
          {/* 枠線（最前面） */}
          <polygon
            points={points}
            fill="none"
            stroke={isHighlightedCity ? 'hsl(142, 76%, 36%)' : 'hsl(var(--foreground))'}
            strokeWidth={isHighlightedCity ? '4' : '2'}
          />
        </>
      ) : (
        <polygon
          className={`
            transition-all duration-150
            ${isTemporary && !isTempNewCity && !hasValidationError ? 'fill-primary/30' : ''}
            ${isTemporary && !isTempNewCity && hasValidationError ? 'fill-destructive/30' : ''}
            ${isClickable && !isTemporary ? 'hover:fill-primary/20' : ''}
          `}
          style={isHighlightedCity ? { animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
          points={points}
          fill={
            isTempNewCity && tempNewCityInfo.color
              ? getCityColorFill(tempNewCityInfo.color)
              : isTemporary
                ? undefined
                : isNewCity && newCityColor
                  ? getCityColorFill(newCityColor)
                  : hex.city
                    ? getCityColorFill(hex.city.color)
                    : getTerrainFill(hex.terrain)
          }
          stroke={
            isHighlightedCity
              ? 'hsl(142, 76%, 36%)'
              : isTemporary
                ? (hasValidationError ? 'hsl(var(--destructive))' : 'hsl(var(--primary))')
                : (hex.city || isNewCity) ? 'hsl(var(--foreground))' : 'hsl(var(--border-track))'
          }
          strokeWidth={isHighlightedCity ? '4' : (isTemporary ? '3' : ((hex.city || isNewCity) ? '2' : '1'))}
        />
      )}

      {/* 都市: 中央に丸 + 大きな数字、都市名は円弧状に配置 */}
      {hex.city && (
        <g transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}>
          {/* 中央の丸（白または黒） */}
          <circle
            cx={centerX}
            cy={centerY}
            r={hexSize * 0.55}
            fill={hex.city.diceColor === 'black' ? 'hsl(var(--foreground))' : 'white'}
            stroke={hex.city.diceColor === 'black' ? 'white' : 'hsl(var(--foreground))'}
            strokeWidth="2"
          />
          {/* 大きなダイス番号（中央）- 空配列の場合は表示しない */}
          {((hex.city.diceNumbers && hex.city.diceNumbers.length > 0) || hex.city.diceNumber) && (
            <text
              className={`text-[14px] font-heading font-bold ${hex.city.diceColor === 'black' ? 'fill-white' : 'fill-foreground'}`}
              x={centerX}
              y={centerY + 5}
              textAnchor="middle"
            >
              {hex.city.diceNumbers && hex.city.diceNumbers.length > 0 ? hex.city.diceNumbers.join(',') : hex.city.diceNumber}
            </text>
          )}
          {/* 都市名は別レイヤーで描画 */}
        </g>
      )}

      {/* 町名表示 */}
      {hex.town && (
        <>
          <circle cx={centerX} cy={centerY} r={hexSize * 0.4} fill="white" stroke="hsl(var(--muted-foreground))" strokeWidth="1" />
          <text
            className="text-[7px] fill-muted-foreground"
            x={centerX}
            y={centerY + 3}
            textAnchor="middle"
            transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}
          >
            {hex.town.id.substring(0, 4)}
          </text>
        </>
      )}

      {/* 座標表示（デバッグ用） */}
      <text
        className="text-[5px] fill-muted-foreground/50"
        x={centerX}
        y={centerY + hexSize * 0.7}
        textAnchor="middle"
        transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}
      >
        {hex.coordinate.q},{hex.coordinate.r}
      </text>

      {/* 商品表示（2倍サイズ、中央数字にかぶらないよう上部に配置）- skipGoodsRendering時はスキップ */}
      {!skipGoodsRendering && hex.goods && hex.goods.length > 0 && (
        <g className="goods-display" transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}>
          {hex.goods.map((goodsColor: GoodsColor, idx: number) => (
            <rect
              key={idx}
              x={centerX + (idx - hex.goods.length / 2 + 0.5) * cubeSpacing - cubeSize / 2}
              y={centerY - hexSize * 0.85}
              width={cubeSize}
              height={cubeSize}
              rx="2"
              fill={getGoodsColorFill(goodsColor)}
              stroke={goodsColor === 'black' ? 'white' : 'hsl(var(--foreground))'}
              strokeWidth="1"
            />
          ))}
        </g>
      )}

      {/* 配置済みタイルの線路描画 */}
      {hex.tile && !isNewCity && (
        <g transform={`rotate(${(hex.tile.rotation || 0) * 60}, ${centerX}, ${centerY})`}>
          {renderTileTracks(
            hex.tile.type,
            centerX,
            centerY,
            hexSize,
            false,
            false,
            isHighlightedTrack,
            // 絶対座標のedgePairを基本座標（回転前）に変換、segmentIndex付き
            // 例: rotation=1, edgePair=[2,5] → base=[1,4] (各辺から回転分を引く)
            highlightedEdgePairs?.map(({ pair, segmentIndex }) => ({
              pair: [
                // -1 は「中央」を表す特別な値なので回転変換しない
                pair[0] === -1 ? -1 : ((pair[0] - (hex.tile?.rotation || 0)) % 6 + 6) % 6,
                pair[1] === -1 ? -1 : ((pair[1] - (hex.tile?.rotation || 0)) % 6 + 6) % 6
              ] as [number, number],
              segmentIndex
            })),
            getTrackColorsByOwners(hex.tile),
            totalHighlightCount
          )}
        </g>
      )}

      {/* Town ヘクスに通常線路タイルを配置した場合、白丸を線路より前面に再描画 */}
      {hex.town && hex.tile && !hex.tile.type.startsWith('TOWN_') && (
        <circle
          cx={centerX}
          cy={centerY}
          r={hexSize * 0.35}
          fill="white"
          stroke="hsl(var(--foreground))"
          strokeWidth="1.5"
        />
      )}

      {/* 新都市: 中央に白丸 + ラベル（A-H）- 最前面に表示 */}
      {isNewCity && newCityName && (
        <g transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}>
          {/* 中央の白丸 */}
          <circle
            cx={centerX}
            cy={centerY}
            r={hexSize * 0.55}
            fill="white"
            stroke="hsl(var(--foreground))"
            strokeWidth="2"
          />
          {/* 新都市ラベル（中央） */}
          <text
            className="text-[14px] font-heading fill-foreground font-bold"
            x={centerX}
            y={centerY + 5}
            textAnchor="middle"
          >
            {newCityName}
          </text>
        </g>
      )}

      {/* 仮配置タイルの描画 */}
      {isTemporary && temporaryTileType && (
        isTempNewCity ? (
          /* 仮配置：新都市タイル - 白丸＋ラベル */
          <g transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}>
            <circle
              cx={centerX}
              cy={centerY}
              r={hexSize * 0.55}
              fill="white"
              stroke={hasValidationError ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'}
              strokeWidth="3"
            />
            <text
              className="text-[14px] font-heading fill-foreground font-bold"
              x={centerX}
              y={centerY + 5}
              textAnchor="middle"
            >
              {tempNewCityInfo.name}
            </text>
          </g>
        ) : (
          /* 仮配置：線路タイル */
          <g transform={`rotate(${(temporaryRotation || 0) * 60}, ${centerX}, ${centerY})`}>
            {renderTileTracks(temporaryTileType, centerX, centerY, hexSize, true, hasValidationError)}
          </g>
        )
      )}

    </g>
  );
}

export default HexCell;
